import React, { useState, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import { storageService } from '../../lib/storageService'
import * as FiIcons from 'react-icons/fi'

const { FiInfo, FiHardDrive } = FiIcons

const StorageInfo = ({ userId }) => {
  const [storageData, setStorageData] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (userId) {
      loadStorageData()
    }
  }, [userId])

  const loadStorageData = async () => {
    try {
      setLoading(true)
      const data = await storageService.getStorageUsage(userId)
      setStorageData(data)
    } catch (error) {
      console.error('Failed to load storage data:', error)
      // Set default values on error
      setStorageData({
        usage_percentage: 0,
        total_bytes_used: 0,
        quota_bytes: 52428800,
        image_count: 0,
        plan_type: 'free'
      })
    } finally {
      setLoading(false)
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 MB'
    const mb = bytes / (1024 * 1024)
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`
  }

  const getStorageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 75) return 'text-yellow-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <SafeIcon icon={FiHardDrive} className="w-4 h-4" />
        <span>Loading...</span>
      </div>
    )
  }

  return (
    <div className="relative">
      <div 
        className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
        onMouseEnter={() => setShowDetails(true)}
        onMouseLeave={() => setShowDetails(false)}
      >
        <SafeIcon icon={FiHardDrive} className="w-4 h-4 text-gray-500" />
        <span className={getStorageColor(storageData.usage_percentage)}>
          Storage: {storageData.usage_percentage}%
        </span>
        <SafeIcon icon={FiInfo} className="w-3 h-3 text-gray-400" />
      </div>

      {/* Details Flyout */}
      {showDetails && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-64 z-50">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Storage Usage</h3>
              <span className={`text-sm font-medium ${getStorageColor(storageData.usage_percentage)}`}>
                {storageData.usage_percentage}%
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  storageData.usage_percentage >= 90 ? 'bg-red-500' :
                  storageData.usage_percentage >= 75 ? 'bg-yellow-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(storageData.usage_percentage, 100)}%` }}
              />
            </div>

            {/* Usage Details */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Used:</span>
                <span className="font-medium">{formatBytes(storageData.total_bytes_used)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Quota:</span>
                <span className="font-medium">{formatBytes(storageData.quota_bytes)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Images:</span>
                <span className="font-medium">{storageData.image_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium capitalize">{storageData.plan_type}</span>
              </div>
            </div>

            {/* Upgrade Notice */}
            {storageData.usage_percentage >= 75 && storageData.plan_type === 'free' && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-xs text-gray-600 mb-2">
                  {storageData.usage_percentage >= 90 ? 'Storage almost full!' : 'Storage getting full'}
                </div>
                <button className="w-full bg-blue-600 text-white text-xs py-2 px-3 rounded hover:bg-blue-700 transition-colors">
                  Upgrade to Pro
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default StorageInfo
